/* ============================================================================
   The studio directory — every supplier and contractor the studio has ever
   worked with, LEARNED automatically as tenders and quotation packages run:
   issuing an RFQ registers the supplier with the package scope as a tag;
   quotes, wins, bids and awards enrich the record. Synced as one "meta"
   aggregate so the whole studio shares it, on every tender.
   ========================================================================== */
import { useEffect, useState } from "react";
import { uid } from "./store";
import type { DirectoryEntry, DirectoryEvent } from "./types";

const KEY = "atelier.directory.v1";
export const DIRECTORY_AGG_ID = "directory";

type State = { entries: DirectoryEntry[] };

function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { entries: [] };
}

let state: State = load();
const subs = new Set<() => void>();

function commit(next: State) {
  state = next;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
  changeListener?.(asAggregate());
}

export function useDirectory(): DirectoryEntry[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return state.entries;
}
export function getDirectory() { return state.entries; }

/* --- sync hooks --------------------------------------------------------- */
export type DirectoryAggregate = { id: string; entries: DirectoryEntry[] };
function asAggregate(): DirectoryAggregate { return { id: DIRECTORY_AGG_ID, entries: state.entries }; }
let changeListener: ((a: DirectoryAggregate) => void) | null = null;
export function onDirectoryChange(f: (a: DirectoryAggregate) => void) { changeListener = f; }
export function getDirectoryAggregates(): DirectoryAggregate[] { return [asAggregate()]; }
export function replaceDirectoryAggregates(aggs: { id: string }[]) {
  const row = aggs.find((a) => a.id === DIRECTORY_AGG_ID) as DirectoryAggregate | undefined;
  if (!row) return;
  state = { entries: row.entries ?? [] };
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
}

/* --- scope tags --------------------------------------------------------- */
const STOP = new Set(["the", "and", "for", "with", "supply", "only", "works", "package", "main", "incl", "per"]);
/** Distil a package/tender title into scope tags — "Aluminium windows &
    curtain walling — supply only" → [aluminium, windows, curtain, walling]. */
export function scopeTags(title: string): string[] {
  return [...new Set(
    title.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/)
      .filter((w) => w.length > 3 && !STOP.has(w)),
  )].slice(0, 8);
}

/** Entries whose learned tags overlap this scope, best matches first. */
export function suggestFor(title: string, kind?: DirectoryEntry["kind"]): DirectoryEntry[] {
  const tags = scopeTags(title);
  if (!tags.length) return [];
  return state.entries
    .filter((e) => !kind || e.kind === kind)
    .map((e) => ({ e, hits: tags.filter((t) => e.tags.includes(t)).length }))
    .filter((x) => x.hits > 0)
    .sort((a, b) => b.hits - a.hits || b.e.history.length - a.e.history.length)
    .map((x) => x.e);
}

/* --- actions ------------------------------------------------------------ */
export const directoryActions = {
  /** Manual registration — dedupes by name+kind, merging contact and tags. */
  add(data: { name: string; email?: string; phone?: string; kind: DirectoryEntry["kind"]; tags?: string[] }): DirectoryEntry {
    const name = data.name.trim();
    const tags = [...new Set((data.tags ?? []).map((x) => x.toLowerCase().trim()).filter(Boolean))].slice(0, 24);
    const i = state.entries.findIndex((e) => e.name.toLowerCase() === name.toLowerCase() && e.kind === data.kind);
    if (i >= 0) {
      const e = state.entries[i];
      const next: DirectoryEntry = { ...e, email: data.email || e.email, phone: data.phone || e.phone, tags: [...new Set([...e.tags, ...tags])].slice(0, 24) };
      commit({ entries: state.entries.map((x, j) => (j === i ? next : x)) });
      return next;
    }
    const entry: DirectoryEntry = { id: uid("dir-"), name, email: data.email?.trim() || undefined, phone: data.phone?.trim() || undefined, kind: data.kind, tags, history: [], addedAt: new Date().toISOString() };
    commit({ entries: [entry, ...state.entries] });
    return entry;
  },

  /** The learning hook — called by store actions as work happens. Upserts by
      name (case-insensitive), merges tags, appends the event (capped). */
  learn(data: {
    name: string; email?: string; phone?: string; kind: DirectoryEntry["kind"];
    scopeTitle: string; event: Omit<DirectoryEvent, "at">;
  }) {
    const name = data.name.trim();
    if (!name) return;
    const tags = scopeTags(data.scopeTitle);
    const ev: DirectoryEvent = { ...data.event, at: new Date().toISOString() };
    const i = state.entries.findIndex((e) => e.name.toLowerCase() === name.toLowerCase() && e.kind === data.kind);
    if (i >= 0) {
      const e = state.entries[i];
      const next: DirectoryEntry = {
        ...e,
        email: data.email || e.email,
        phone: data.phone || e.phone,
        tags: [...new Set([...e.tags, ...tags])].slice(0, 24),
        history: [ev, ...e.history].slice(0, 40),
      };
      commit({ entries: state.entries.map((x, j) => (j === i ? next : x)) });
    } else {
      commit({
        entries: [
          { id: uid("dir-"), name, email: data.email, phone: data.phone, kind: data.kind, tags, history: [ev], addedAt: new Date().toISOString() },
          ...state.entries,
        ],
      });
    }
  },
  update(id: string, patch: Partial<Pick<DirectoryEntry, "name" | "email" | "phone" | "tags" | "note">>) {
    commit({ entries: state.entries.map((e) => (e.id === id ? { ...e, ...patch } : e)) });
  },
  remove(id: string) {
    commit({ entries: state.entries.filter((e) => e.id !== id) });
  },
};
